import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import TrialExpiredWall from '@/components/billing/TrialExpiredWall';

interface ProtectedRouteProps {
  children: ReactNode;
  /** set on setup/onboarding routes that must stay reachable after a trial ends */
  allowExpired?: boolean;
}

const ProtectedRoute = ({ children, allowExpired = false }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const { isTrialExpired, tenantId, isLoading: subLoading } = useSubscription();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-accent/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // A business must be created or joined before location setup can begin.
  if (!allowExpired && !subLoading && !tenantId) {
    return <Navigate to="/onboarding" replace />;
  }

  if (!allowExpired && !subLoading && isTrialExpired) {
    return <TrialExpiredWall />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
